// Microsoft Teams integration. Runs ONCE, before the app renders, and only when
// the app was opened as a Teams tab (the tab URL carries ?inTeams=1). In an
// ordinary browser none of this runs, so the normal login page is unaffected.
//
// When opened from Teams it:
//   1. signs the person in with their Microsoft identity (Teams single sign-on)
//      if they are not already signed in, using the same session storage the
//      login form writes to; and
//   2. opens the page a Teams alert asked for (a "deep link"), if any.
//
// Every failure is swallowed: the worst case is the normal login page.

const FLAG = "i_ems_in_teams";

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error("timed out")), ms);
    Promise.resolve(promise).then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); }
    );
  });
}

// True when this tab was opened by Teams. The flag survives in-app navigation
// (which drops the query string) for the rest of the session.
export function openedInTeams() {
  try {
    if (new URLSearchParams(window.location.search).get("inTeams") === "1") sessionStorage.setItem(FLAG, "1");
    return sessionStorage.getItem(FLAG) === "1";
  } catch (e) {
    return false;
  }
}

// An I-EMS session token is a JWT; treat one that has expired as absent.
export function tokenUsable(token) {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return !payload.exp || payload.exp * 1000 > Date.now() + 60 * 1000;
  } catch (e) {
    return false;
  }
}

// Only ever navigate to a page this app actually has; anything else is ignored.
export function safePath(path, allowedPaths) {
  return typeof path === "string" && allowedPaths.includes(path) ? path : null;
}

export async function bootstrapTeams({ tokenKey, profileKey, ssoUrl, allowedPaths }) {
  if (!openedInTeams()) return { inTeams: false };

  const result = { inTeams: true, signedIn: false, target: null };
  let app = null;
  try {
    // Loaded only when opened from Teams, so ordinary visitors never fetch it.
    const sdk = await import("@microsoft/teams-js");
    app = sdk.app;
    const authentication = sdk.authentication;
    await withTimeout(app.initialize(), 5000);

    let target = null;
    try {
      const context = await withTimeout(app.getContext(), 4000);
      target = safePath(context?.page?.subPageId, allowedPaths);
    } catch (e) {
      // no context: just no deep link
    }

    if (!tokenUsable(localStorage.getItem(tokenKey))) {
      try {
        const microsoftToken = await withTimeout(authentication.getAuthToken(), 10000);
        const res = await withTimeout(
          fetch(ssoUrl, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: microsoftToken }) }),
          10000
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && json?.data?.token) {
          localStorage.setItem(tokenKey, json.data.token);
          localStorage.setItem(profileKey, JSON.stringify(json.data.profile || null));
        }
      } catch (e) {
        // not signed in: the normal login page will show
      }
    }

    result.signedIn = tokenUsable(localStorage.getItem(tokenKey));
    if (result.signedIn && target) {
      window.history.replaceState(null, "", target);
      result.target = target;
    }
  } catch (e) {
    // Teams SDK unavailable: carry on as a normal web page
  } finally {
    try { if (app) app.notifySuccess(); } catch (e) { /* not inside Teams */ }
  }
  return result;
}
